// ---------------------------------------------------------------------------
// @firesquirrel/core-domain — Canonical rules engine
// Evaluates InspectionRule[] against current field responses.
// Supports compound and/or/not conditions and all action types.
// ---------------------------------------------------------------------------

import type {
  Condition,
  Operator,
  InspectionForm,
  InspectionFormField,
  InspectionRule,
  RuleAction,
  ResolvedFieldState,
  ResolvedFieldStateV2,
  ResolvedSectionState,
  ResolvedFormState,
  ResolvedFormStateV2,
  RuleTraceEntry,
  FieldResponse,
  ViolationMapping,
} from './types';

// Helper to resolve un-answered field default value for rule evaluation
function resolveRuleFieldDefault(field: InspectionFormField): unknown {
  if (field.defaultValue !== undefined && field.defaultValue !== null) {
    if (field.type === 'toggle' && typeof field.defaultValue === 'string') {
      const lower = field.defaultValue.trim().toLowerCase();
      if (lower === 'false' || lower === '0' || lower === 'off' || lower === 'no') return false;
      if (lower === 'true' || lower === '1' || lower === 'on' || lower === 'yes') return true;
    }
    return field.defaultValue;
  }
  const extras = (field.extras ?? {}) as Record<string, unknown>;
  if (field.type === 'toggle') {
    const ds = extras.defaultState;
    if (ds === 'on') return true;
    if (ds === 'off') return false;
  }
  return undefined;
}

function buildFieldDefaultsMap(forms: InspectionForm | readonly InspectionForm[]): Record<string, unknown> {
  const defaultsMap: Record<string, unknown> = {};
  const formList = Array.isArray(forms) ? forms : [forms];
  for (const form of formList) {
    for (const field of form.fields ?? []) {
      const dv = resolveRuleFieldDefault(field);
      if (dv !== undefined) {
        defaultsMap[field.id] = dv;
      }
    }
  }
  return defaultsMap;
}

// ---------------------------------------------------------------------------
// Evaluate a single leaf condition
// ---------------------------------------------------------------------------

function evalLeaf(
  field: string,
  op: Operator,
  condValue: unknown,
  responses: Record<string, FieldResponse>,
  fieldDefaults?: Record<string, unknown>,
): boolean {
  const resp = responses[field];
  const v = resp?.value !== undefined ? resp.value : fieldDefaults?.[field];

  switch (op) {
    case 'equals':
      if (typeof v === 'boolean' || typeof condValue === 'boolean' || condValue === 'true' || condValue === 'false' || condValue === '0' || condValue === '1') {
        const vBool = typeof v === 'boolean' ? v : (String(v).trim().toLowerCase() === 'true' || String(v) === '1');
        const condBool = typeof condValue === 'boolean' ? condValue : (String(condValue).trim().toLowerCase() === 'true' || String(condValue) === '1');
        return vBool === condBool;
      }
      return v === condValue || (v != null && condValue != null && String(v) === String(condValue));
    case 'notEquals':
      if (typeof v === 'boolean' || typeof condValue === 'boolean' || condValue === 'true' || condValue === 'false' || condValue === '0' || condValue === '1') {
        const vBool = typeof v === 'boolean' ? v : (String(v).trim().toLowerCase() === 'true' || String(v) === '1');
        const condBool = typeof condValue === 'boolean' ? condValue : (String(condValue).trim().toLowerCase() === 'true' || String(condValue) === '1');
        return vBool !== condBool;
      }
      return v !== condValue && (v == null || condValue == null || String(v) !== String(condValue));
    case 'in':
      return Array.isArray(condValue) && condValue.includes(v);
    case 'notIn':
      return Array.isArray(condValue) && !condValue.includes(v);
    case 'gt':
      if (typeof v === 'number' && typeof condValue === 'number') return v > condValue;
      if (typeof v === 'string' && typeof condValue === 'string') return v > condValue;
      return false;
    case 'gte':
      if (typeof v === 'number' && typeof condValue === 'number') return v >= condValue;
      if (typeof v === 'string' && typeof condValue === 'string') return v >= condValue;
      return false;
    case 'lt':
      if (typeof v === 'number' && typeof condValue === 'number') return v < condValue;
      if (typeof v === 'string' && typeof condValue === 'string') return v < condValue;
      return false;
    case 'lte':
      if (typeof v === 'number' && typeof condValue === 'number') return v <= condValue;
      if (typeof v === 'string' && typeof condValue === 'string') return v <= condValue;
      return false;
    case 'contains':
      if (typeof v === 'string') return v.includes(String(condValue ?? ''));
      if (Array.isArray(v)) return v.includes(condValue);
      return false;
    case 'exists':
      return v !== undefined && v !== null && v !== '';
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Evaluate a Condition tree (recursive)
// ---------------------------------------------------------------------------

export function evaluateCondition(
  condition: Condition,
  responses: Record<string, FieldResponse>,
  fieldDefaults?: Record<string, unknown>,
): boolean {
  if ('and' in condition) {
    return condition.and.every((c) => evaluateCondition(c, responses, fieldDefaults));
  }
  if ('or' in condition) {
    return condition.or.some((c) => evaluateCondition(c, responses, fieldDefaults));
  }
  if ('not' in condition) {
    return !evaluateCondition(condition.not, responses, fieldDefaults);
  }
  // Leaf condition
  return evalLeaf(condition.field, condition.op, condition.value, responses, fieldDefaults);
}

// ---------------------------------------------------------------------------
// Apply a single rule action to the accumulated state maps
// ---------------------------------------------------------------------------

function applyAction(
  action: RuleAction,
  fields: Record<string, ResolvedFieldState>,
  sections: Record<string, ResolvedSectionState>,
  forms: Record<string, { hidden: boolean }>,
): void {
  switch (action.action) {
    case 'hideField':
      if (fields[action.fieldId]) fields[action.fieldId].hidden = true;
      break;
    case 'showField':
      if (fields[action.fieldId]) fields[action.fieldId].hidden = false;
      break;
    case 'hideSection':
      if (sections[action.sectionId]) sections[action.sectionId].hidden = true;
      break;
    case 'showSection':
      if (sections[action.sectionId]) sections[action.sectionId].hidden = false;
      break;
    case 'hideForm':
      if (forms[action.formId]) forms[action.formId].hidden = true;
      break;
    case 'showForm':
      if (forms[action.formId]) forms[action.formId].hidden = false;
      break;
    case 'setOptions':
      if (fields[action.fieldId]) fields[action.fieldId].options = action.options;
      break;
    case 'require':
      if (fields[action.fieldId]) fields[action.fieldId].required = true;
      break;
  }
}

// ---------------------------------------------------------------------------
// Evaluate all rules for a single form and return resolved state
// ---------------------------------------------------------------------------

export function evaluateFormRules(
  form: InspectionForm,
  responses: Record<string, FieldResponse>,
): ResolvedFormState {
  const fieldDefaults = buildFieldDefaultsMap(form);

  // Build initial field states from initialVisibility
  const fieldStates: Record<string, ResolvedFieldState> = {};
  for (const field of form.fields) {
    fieldStates[field.id] = {
      hidden: field.initialVisibility === 'hidden',
      required: field.required ?? false,
      disabled: false,
    };
  }

  // Build initial section states
  const sectionStates: Record<string, ResolvedSectionState> = {};
  for (const section of form.layout.sections) {
    sectionStates[section.id] = {
      hidden: section.initialVisibility === 'hidden',
    };
  }

  // Form-level hidden state (used for cross-form rules)
  const formState = { hidden: form.initialVisibility === 'hidden' };

  // Evaluate each rule
  if (form.rules) {
    for (const rule of form.rules) {
      if (evaluateCondition(rule.when, responses, fieldDefaults)) {
        for (const action of rule.then) {
          applyAction(action, fieldStates, sectionStates, { [form._id]: formState });
        }
      }
    }
  }

  return {
    hidden: formState.hidden,
    fields: fieldStates,
    sections: sectionStates,
  };
}

// ---------------------------------------------------------------------------
// Evaluate rules across multiple forms (for cross-form dependencies)
// ---------------------------------------------------------------------------

export function evaluateAllFormRules(
  forms: InspectionForm[],
  responses: Record<string, FieldResponse>,
): Record<string, ResolvedFormState> {
  const fieldDefaults = buildFieldDefaultsMap(forms);

  // Build initial states for all forms
  const allFieldStates: Record<string, ResolvedFieldState> = {};
  const allSectionStates: Record<string, ResolvedSectionState> = {};
  const allFormStates: Record<string, { hidden: boolean }> = {};

  for (const form of forms) {
    allFormStates[form._id] = { hidden: form.initialVisibility === 'hidden' };
    for (const field of form.fields) {
      allFieldStates[field.id] = {
        hidden: field.initialVisibility === 'hidden',
        required: field.required ?? false,
        disabled: false,
      };
    }
    for (const section of form.layout.sections) {
      allSectionStates[section.id] = {
        hidden: section.initialVisibility === 'hidden',
      };
    }
  }

  // Evaluate all rules across all forms
  for (const form of forms) {
    if (!form.rules) continue;
    for (const rule of form.rules) {
      if (evaluateCondition(rule.when, responses, fieldDefaults)) {
        for (const action of rule.then) {
          applyAction(action, allFieldStates, allSectionStates, allFormStates);
        }
      }
    }
  }

  for (const form of forms) {
    for (const dependency of form.crossFormDependencies ?? []) {
      if (!evaluateCondition(dependency.condition, responses, fieldDefaults)) continue;

      if (dependency.targetFieldId && allFieldStates[dependency.targetFieldId]) {
        switch (dependency.actionType) {
          case 'hide':
            allFieldStates[dependency.targetFieldId].hidden = true;
            break;
          case 'show':
            allFieldStates[dependency.targetFieldId].hidden = false;
            break;
          case 'require':
            allFieldStates[dependency.targetFieldId].required = true;
            break;
          case 'disable':
            allFieldStates[dependency.targetFieldId].disabled = true;
            break;
        }
        continue;
      }

      if (allFormStates[dependency.targetFormId]) {
        switch (dependency.actionType) {
          case 'hide':
            allFormStates[dependency.targetFormId].hidden = true;
            break;
          case 'show':
            allFormStates[dependency.targetFormId].hidden = false;
            break;
        }
      }
    }
  }

  // Package results per form
  const result: Record<string, ResolvedFormState> = {};
  for (const form of forms) {
    const fieldStates: Record<string, ResolvedFieldState> = {};
    for (const field of form.fields) {
      fieldStates[field.id] = allFieldStates[field.id];
    }
    const sectionStates: Record<string, ResolvedSectionState> = {};
    for (const section of form.layout.sections) {
      sectionStates[section.id] = allSectionStates[section.id];
    }
    result[form._id] = {
      hidden: allFormStates[form._id].hidden,
      fields: fieldStates,
      sections: sectionStates,
    };
  }

  return result;
}

// ---------------------------------------------------------------------------
// Check if a field value triggers a violation mapping
// ---------------------------------------------------------------------------

export function isFieldFailed(
  field: InspectionFormField,
  responses: Record<string, FieldResponse>,
): boolean {
  // Back-compat shim. Engine v1 only inspected mappings[0] (B2 bug). Engine v2
  // walks every mapping via `getFailingMappings` so callers that already
  // imported `isFieldFailed` get the corrected semantics for free.
  return getFailingMappings(field, responses).length > 0;
}

// ===========================================================================
// PHASE 2 — ENGINE v2 (B2 + B3 + B4 + B16 + G-024 fixes, with trace)
// ===========================================================================
//
// Coexists with v1 above. Consumers migrate at their own pace. Phase 7 removes
// v1 after every caller has switched.

// ---------------------------------------------------------------------------
// B2 fix — multi-mapping evaluation.
// ---------------------------------------------------------------------------

/**
 * Return every ViolationMapping whose `trigger` matches the current responses.
 *
 * Mappings with `bindingMode === 'pending-tenant-bind'` are skipped per G-024
 * (the tenant admin has not resolved the binding yet, so the mapping cannot
 * legitimately fire).
 */
export function getFailingMappings(
  field: InspectionFormField,
  responses: Record<string, FieldResponse>,
): ViolationMapping[] {
  if (!field.violationMappings?.length) return [];
  const out: ViolationMapping[] = [];
  for (const vm of field.violationMappings) {
    if (vm.bindingMode === 'pending-tenant-bind') continue;
    if (evaluateCondition(vm.trigger, responses)) out.push(vm);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Engine v2 — `applyActionV2` honors disable/enable + carries requireNotes /
// requirePhotos payload (B3 + B4).
// ---------------------------------------------------------------------------

function applyActionV2(
  action: RuleAction,
  fields: Record<string, ResolvedFieldStateV2>,
  sections: Record<string, ResolvedSectionState>,
  forms: Record<string, { hidden: boolean }>,
  visibilityAlwaysSet: ReadonlySet<string>,
): void {
  switch (action.action) {
    case 'hideField':
      if (fields[action.fieldId] && !visibilityAlwaysSet.has(action.fieldId)) {
        fields[action.fieldId].hidden = true;
      }
      break;
    case 'showField':
      if (fields[action.fieldId]) fields[action.fieldId].hidden = false;
      break;
    case 'hideSection':
      if (sections[action.sectionId]) sections[action.sectionId].hidden = true;
      break;
    case 'showSection':
      if (sections[action.sectionId]) sections[action.sectionId].hidden = false;
      break;
    case 'hideForm':
      if (forms[action.formId]) forms[action.formId].hidden = true;
      break;
    case 'showForm':
      if (forms[action.formId]) forms[action.formId].hidden = false;
      break;
    case 'setOptions':
      if (fields[action.fieldId]) fields[action.fieldId].options = action.options;
      break;
    case 'require':
      if (fields[action.fieldId]) {
        fields[action.fieldId].required = true;
        if (action.requireNotes !== undefined) fields[action.fieldId].requireNotes = action.requireNotes;
        if (action.requirePhotos !== undefined) fields[action.fieldId].requirePhotos = action.requirePhotos;
        if (action.showNotes !== undefined) fields[action.fieldId].showNotes = action.showNotes;
        if (action.showPhotos !== undefined) fields[action.fieldId].showPhotos = action.showPhotos;
      }
      break;
    case 'disable':
      if (fields[action.fieldId]) fields[action.fieldId].disabled = true;
      break;
    case 'enable':
      if (fields[action.fieldId]) fields[action.fieldId].disabled = false;
      break;
  }
}

// ---------------------------------------------------------------------------
// B16 — collect fieldIds whose `visibility === 'always'` to short-circuit
// hideField actions for those fields.
// ---------------------------------------------------------------------------

function collectAlwaysVisibleFields(form: InspectionForm): Set<string> {
  const s = new Set<string>();
  for (const field of form.fields) {
    if (field.visibility === 'always') s.add(field.id);
  }
  return s;
}

// ---------------------------------------------------------------------------
// Best-effort unmatched-reason inference. Walks the condition tree to find
// the first leaf whose evaluation came back false; returns it as a hint for
// the Data slide-in Rules Tracker view.
// ---------------------------------------------------------------------------

function findFailingLeaf(
  condition: Condition,
  responses: Record<string, FieldResponse>,
): { field: string; op: Operator; value?: unknown; actualValue: unknown } | null {
  if ('and' in condition) {
    for (const c of condition.and) {
      if (!evaluateCondition(c, responses)) {
        const inner = findFailingLeaf(c, responses);
        if (inner) return inner;
      }
    }
    return null;
  }
  if ('or' in condition) {
    // `or` matches when ANY child is true; if it overall failed, every child
    // failed — report the first.
    for (const c of condition.or) {
      const inner = findFailingLeaf(c, responses);
      if (inner) return inner;
    }
    return null;
  }
  if ('not' in condition) {
    // A `not` fails when its inner is true; the most useful pointer is the
    // inner leaf that *did* match.
    return null;
  }
  const actualValue = responses[condition.field]?.value;
  return { field: condition.field, op: condition.op, value: condition.value, actualValue };
}

// ---------------------------------------------------------------------------
// Engine v2 — evaluate a single form's rules with full trace + B3 carriers.
// ---------------------------------------------------------------------------

export interface EvaluateRulesV2Opts {
  /** ISO timestamp for trace entries. Defaults to `new Date().toISOString()`. */
  now?: string;
}

export function evaluateFormRulesWithTrace(
  form: InspectionForm,
  responses: Record<string, FieldResponse>,
  opts: EvaluateRulesV2Opts = {},
): ResolvedFormStateV2 {
  const now = opts.now ?? new Date().toISOString();
  const visibilityAlwaysSet = collectAlwaysVisibleFields(form);
  const fieldDefaults = buildFieldDefaultsMap(form);

  const fields: Record<string, ResolvedFieldStateV2> = {};
  for (const field of form.fields) {
    fields[field.id] = {
      // B16 — visibility:'always' overrides initialVisibility:'hidden'.
      hidden: !visibilityAlwaysSet.has(field.id) && field.initialVisibility === 'hidden',
      required: field.required ?? false,
      disabled: false,
    };
  }

  const sections: Record<string, ResolvedSectionState> = {};
  for (const section of form.layout.sections) {
    sections[section.id] = { hidden: section.initialVisibility === 'hidden' };
  }

  const formState = { hidden: form.initialVisibility === 'hidden' };
  const trace: RuleTraceEntry[] = [];

  for (const rule of form.rules ?? []) {
    const matched = evaluateCondition(rule.when, responses, fieldDefaults);
    const entry: RuleTraceEntry = {
      ruleId: rule.id,
      ruleName: rule.name,
      scope: rule.scope ?? 'form',
      matched,
      conditionDump: rule.when,
      appliedActions: matched ? rule.then : [],
      evaluatedAt: now,
    };
    if (matched) {
      for (const action of rule.then) {
        applyActionV2(action, fields, sections, { [form._id]: formState }, visibilityAlwaysSet);
      }
    } else {
      const leaf = findFailingLeaf(rule.when, responses);
      if (leaf) {
        entry.unmatchedBecause = {
          failingLeaf: { field: leaf.field, op: leaf.op, value: leaf.value },
          actualValue: leaf.actualValue,
        };
      }
    }
    trace.push(entry);
  }

  return {
    hidden: formState.hidden,
    fields,
    sections,
    trace,
  };
}

// ---------------------------------------------------------------------------
// Engine v2 — evaluate across multiple forms, including cross-form deps and
// the template-scope rules merged in (when supplied).
//
// Cross-form deps live on `form.crossFormDependencies` and existed in v1; v2
// preserves them. The shape is the same; only the result type is enriched.
// ---------------------------------------------------------------------------

export function evaluateAllFormRulesWithTrace(
  forms: InspectionForm[],
  responses: Record<string, FieldResponse>,
  templateRules?: readonly InspectionRule[] | null,
  opts: EvaluateRulesV2Opts = {},
): Record<string, ResolvedFormStateV2> {
  const now = opts.now ?? new Date().toISOString();
  const fieldDefaults = buildFieldDefaultsMap(forms);

  // Per-form initial states + always-visible sets.
  const allFieldStates: Record<string, ResolvedFieldStateV2> = {};
  const allSectionStates: Record<string, ResolvedSectionState> = {};
  const allFormStates: Record<string, { hidden: boolean }> = {};
  const alwaysVisible = new Set<string>();
  const perFormTrace: Record<string, RuleTraceEntry[]> = {};

  for (const form of forms) {
    allFormStates[form._id] = { hidden: form.initialVisibility === 'hidden' };
    perFormTrace[form._id] = [];
    for (const field of form.fields) {
      if (field.visibility === 'always') alwaysVisible.add(field.id);
      allFieldStates[field.id] = {
        hidden: field.visibility === 'always' ? false : field.initialVisibility === 'hidden',
        required: field.required ?? false,
        disabled: false,
      };
    }
    for (const section of form.layout.sections) {
      allSectionStates[section.id] = { hidden: section.initialVisibility === 'hidden' };
    }
  }

  // Form-scope rules — trace per owning form.
  for (const form of forms) {
    for (const rule of form.rules ?? []) {
      const matched = evaluateCondition(rule.when, responses, fieldDefaults);
      const entry: RuleTraceEntry = {
        ruleId: rule.id,
        ruleName: rule.name,
        scope: rule.scope ?? 'form',
        matched,
        conditionDump: rule.when,
        appliedActions: matched ? rule.then : [],
        evaluatedAt: now,
      };
      if (matched) {
        for (const action of rule.then) {
          applyActionV2(action, allFieldStates, allSectionStates, allFormStates, alwaysVisible);
        }
      } else {
        const leaf = findFailingLeaf(rule.when, responses);
        if (leaf) {
          entry.unmatchedBecause = {
            failingLeaf: { field: leaf.field, op: leaf.op, value: leaf.value },
            actualValue: leaf.actualValue,
          };
        }
      }
      perFormTrace[form._id].push(entry);
    }
  }

  // Template-scope rules — attribute the trace to the form that owns the
  // most-recently-changed target where determinable; otherwise attribute to
  // the first form in display order so the trace is always reachable in the
  // Rules Tracker view.
  const fallbackFormId = forms[0]?._id;
  for (const rule of templateRules ?? []) {
    const matched = evaluateCondition(rule.when, responses, fieldDefaults);
    const entry: RuleTraceEntry = {
      ruleId: rule.id,
      ruleName: rule.name,
      scope: 'template',
      matched,
      conditionDump: rule.when,
      appliedActions: matched ? rule.then : [],
      evaluatedAt: now,
    };
    if (matched) {
      for (const action of rule.then) {
        applyActionV2(action, allFieldStates, allSectionStates, allFormStates, alwaysVisible);
      }
    } else {
      const leaf = findFailingLeaf(rule.when, responses);
      if (leaf) {
        entry.unmatchedBecause = {
          failingLeaf: { field: leaf.field, op: leaf.op, value: leaf.value },
          actualValue: leaf.actualValue,
        };
      }
    }
    if (fallbackFormId) perFormTrace[fallbackFormId].push(entry);
  }

  // Cross-form dependencies (existing v1 behavior; v2 preserves it).
  for (const form of forms) {
    for (const dependency of form.crossFormDependencies ?? []) {
      if (!evaluateCondition(dependency.condition, responses)) continue;
      if (dependency.targetFieldId && allFieldStates[dependency.targetFieldId]) {
        switch (dependency.actionType) {
          case 'hide':
            if (!alwaysVisible.has(dependency.targetFieldId)) {
              allFieldStates[dependency.targetFieldId].hidden = true;
            }
            break;
          case 'show':
            allFieldStates[dependency.targetFieldId].hidden = false;
            break;
          case 'require':
            allFieldStates[dependency.targetFieldId].required = true;
            break;
          case 'disable':
            allFieldStates[dependency.targetFieldId].disabled = true;
            break;
        }
        continue;
      }
      if (allFormStates[dependency.targetFormId]) {
        switch (dependency.actionType) {
          case 'hide':
            allFormStates[dependency.targetFormId].hidden = true;
            break;
          case 'show':
            allFormStates[dependency.targetFormId].hidden = false;
            break;
        }
      }
    }
  }

  // Package per-form results.
  const result: Record<string, ResolvedFormStateV2> = {};
  for (const form of forms) {
    const fieldStates: Record<string, ResolvedFieldStateV2> = {};
    for (const field of form.fields) fieldStates[field.id] = allFieldStates[field.id];
    const sectionStates: Record<string, ResolvedSectionState> = {};
    for (const section of form.layout.sections) sectionStates[section.id] = allSectionStates[section.id];
    result[form._id] = {
      hidden: allFormStates[form._id].hidden,
      fields: fieldStates,
      sections: sectionStates,
      trace: perFormTrace[form._id],
    };
  }

  return result;
}
